import { BaseNode, type NodeConfig } from 'mflows'
import type { ResourceItem } from '@/store/resource.store'
import { getResources } from '@/store/resource.store'

interface ResourceGetConfig extends NodeConfig {
  resourceId?: string
}

interface ResourceGetValue {
  resource: ResourceItem | null
  content?: any
}

export class ResourceGet extends BaseNode<ResourceGetConfig, ResourceGetValue> {
  static NodeType = '资源读取'

  protected async initialize(): Promise<void> {
    this.setInputPorts([
      { id: 'execute', name: '执行', allowTypes: ['flow'], dataType: 'flow' },
      { id: 'resource-id', name: '资源ID', allowTypes: ['text', 'string'], dataType: 'string' }
    ])

    this.setOutputPorts([
      { id: 'resource', name: '资源', dataType: 'object' },
      { id: 'resource-error', name: '错误', dataType: 'object' },
      { id: 'next', name: '下一步', dataType: 'flow' }
    ])
  }

  protected receiveData(pointId: string, data: unknown): void {
    switch (pointId) {
      case 'resource-id':
        if (typeof data === 'string' && data) {
          this.updateSettings({ resourceId: data })
        }
        break
    }

    const inputPoint = this.findInputPort(pointId)
    if (inputPoint?.dataType === 'flow') {
      this.executeConfigUpdate()
    }
  }

  protected checkConfig(): boolean {
    return Boolean(this.settings.resourceId)
  }

  protected async processConfigUpdate(settings: ResourceGetConfig): Promise<any> {
    try {
      const all = getResources('all')
      const res = all.find(r => r.id === settings.resourceId) || null
      if (!res) {
        const err = { message: '资源不存在', resourceId: settings.resourceId }
        return { value: err, error: new Error(JSON.stringify(err)), state: 'error', updateTime: Date.now() }
      }

      const value: ResourceGetValue = { resource: res }

      // 如果是 JSON 且存在 url，尝试加载内容
      if (res.type === 'json' && res.url) {
        try {
          const resp = await fetch(res.url)
          const text = await resp.text()
          try {
            value.content = JSON.parse(text)
          } catch {
            value.content = text
          }
        } catch (e) {
          // 忽略内容加载失败，仅返回元信息
        }
      }

      Object.assign(this.nodeData, { value })
      return this.nodeData
    } catch (error) {
      return {
        value: { message: String(error) },
        error: error instanceof Error ? error : new Error(String(error)),
        state: 'error',
        updateTime: Date.now()
      }
    }
  }

  protected getOutputPortActions(): { dataOnly?: string[]; triggerFlow?: string[] } {
    if (this.getState() === 'error') {
      return { dataOnly: ['resource-error'], triggerFlow: [] }
    }
    return { dataOnly: ['resource'], triggerFlow: ['next'] }
  }
}